import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", accessToken: null, tier: null, error: null });
  const attemptsRef = useRef(0);
  const MAX_ATTEMPTS = 30;

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session_id" });
      return;
    }

    let cancelled = false;
    let timer = null;

    async function poll() {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        const d = res.data;
        if (d.payment_status === "paid" && d.access_token) {
          // Persist access_token for /new
          try {
            localStorage.setItem(
              "govbidiq_access",
              JSON.stringify({ token: d.access_token, tier: d.tier, session_id: sessionId })
            );
          } catch (_) { /* ignore */ }
          setState({ status: "paid", accessToken: d.access_token, tier: d.tier });
          return;
        }
        if (d.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout" });
          return;
        }
        timer = setTimeout(poll, 2500);
      } catch (e) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "error", error: e?.response?.data?.detail || "Status check failed" });
          return;
        }
        timer = setTimeout(poll, 2500);
      }
    }

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className="grain min-h-screen">
      <Navbar />
      <div className="max-w-xl mx-auto px-6 py-20">
        <div className="panel-elev p-8">
          {state.status === "polling" && (
            <>
              <Loader2 className="w-8 h-8 text-sky-400 animate-spin mb-5" />
              <div className="tick-label text-sky-400 mb-3">/ Verifying payment</div>
              <h1 className="font-display font-black text-2xl text-slate-100 mb-2">
                Confirming your purchase...
              </h1>
              <p className="text-slate-400 text-sm">
                We&apos;re verifying the payment with Stripe. This usually takes a few seconds.
              </p>
            </>
          )}

          {state.status === "paid" && (
            <>
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-5" strokeWidth={1.5} />
              <div className="tick-label text-emerald-400 mb-3">/ Payment confirmed</div>
              <h1 className="font-display font-black text-2xl text-slate-100 mb-2">
                You&apos;re ready to run your analysis.
              </h1>
              <p className="text-slate-400 text-sm mb-6">
                Your access token has been issued for tier{" "}
                <span className="font-mono text-slate-200">{state.tier}</span>. Click below to
                start your analysis — the token will be consumed on submission.
              </p>
              <button
                data-testid="start-paid-analysis"
                onClick={() => nav("/run-analysis")}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3 transition-colors"
              >
                Start Analysis <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </button>
            </>
          )}

          {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
            <>
              <AlertTriangle className="w-10 h-10 text-rose-400 mb-5" />
              <div className="tick-label text-rose-400 mb-3">/ Payment not confirmed</div>
              <h1 className="font-display font-black text-2xl text-slate-100 mb-2">
                We could not verify the payment.
              </h1>
              <p className="text-slate-400 text-sm mb-6">
                {state.error || "The Stripe session may have expired or the payment did not complete."}
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-sky-500 hover:text-sky-400 text-slate-200 font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3"
              >
                Back to Pricing
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
